/**
 * Editing an accepted member — everything except the two things that are not
 * profile fields.
 *
 * The email is a credential and lives in its own dialog with its own
 * confirmation step. The system role goes through its own endpoint, because the
 * owner guard belongs at that boundary and not in a form: the owner's role can
 * only move by transferring ownership, in either direction.
 */

import { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { DESIGNATIONS, SYSTEM_ROLES, type Designation, type SystemRole } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { describeError } from '@/lib/errorMessage';
import { useTeams, useUpdateRole, useUpdateUser, type PersonView } from '../api';

function humanise(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function EditProfileDialog({
  person,
  canChangeRole,
  open,
  onOpenChange,
}: {
  person: PersonView;
  canChangeRole: boolean;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) {
  const { data: teams } = useTeams();
  const updateUser = useUpdateUser();
  const updateRole = useUpdateRole();

  const [name, setName] = useState(person.name);
  const [designation, setDesignation] = useState<Designation>(person.designation);
  const [capacity, setCapacity] = useState(String(person.capacityHoursPerWeek));
  const [teamIds, setTeamIds] = useState<string[]>(person.teamIds);
  const [systemRole, setSystemRole] = useState<SystemRole>(person.systemRole);
  const [error, setError] = useState<string | null>(null);

  const pending = updateUser.isPending || updateRole.isPending;

  function toggleTeam(id: string) {
    setTeamIds((current) =>
      current.includes(id) ? current.filter((t) => t !== id) : [...current, id],
    );
  }

  async function submit() {
    setError(null);
    try {
      await updateUser.mutateAsync({
        userId: person._id,
        patch: {
          name: name.trim(),
          designation,
          teamIds,
          capacityHoursPerWeek: Number(capacity),
        },
      });

      // Separate call, separate guard. Only when it actually changed, so an
      // ordinary profile edit never touches the role endpoint at all.
      if (canChangeRole && systemRole !== person.systemRole) {
        await updateRole.mutateAsync({ userId: person._id, systemRole });
      }

      toast.success(`${name.trim()} updated`);
      onOpenChange(false);
    } catch (err) {
      setError(describeError(err, 'The profile could not be saved'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="text-[16px] font-semibold">Edit {person.name}</DialogTitle>
        <p className="mb-4 mt-0.5 text-[12px] vt-muted">
          Designation is their craft — it sets default capacity and landing view, never permissions.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="profile-name">Name</Label>
            <Input
              id="profile-name"
              data-testid="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profile-designation">Designation</Label>
              <select
                id="profile-designation"
                data-testid="profile-designation"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={designation}
                onChange={(e) => setDesignation(e.target.value as Designation)}
              >
                {DESIGNATIONS.map((d) => (
                  <option key={d} value={d}>
                    {humanise(d)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profile-capacity">Capacity (hours/week)</Label>
              <Input
                id="profile-capacity"
                data-testid="profile-capacity"
                type="number"
                min={0}
                max={80}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
            </div>
          </div>

          {canChangeRole && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profile-role">System role</Label>
              <select
                id="profile-role"
                data-testid="profile-role"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={systemRole}
                onChange={(e) => setSystemRole(e.target.value as SystemRole)}
              >
                {SYSTEM_ROLES.filter((r) => r !== 'owner').map((r) => (
                  <option key={r} value={r}>
                    {humanise(r)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Teams</Label>
            {teams && teams.length > 0 ? (
              <div className="flex flex-wrap gap-2" data-testid="profile-teams">
                {teams.map((team) => (
                  <label key={team._id} className="flex items-center gap-1.5 text-[13px]">
                    <input
                      type="checkbox"
                      checked={teamIds.includes(team._id)}
                      onChange={() => toggleTeam(team._id)}
                    />
                    {team.name}
                  </label>
                ))}
              </div>
            ) : (
              <span className="text-[12px] vt-muted">No teams yet.</span>
            )}
          </div>

          {error && (
            <p
              role="alert"
              data-testid="profile-error"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-testid="save-profile"
              onClick={() => void submit()}
              disabled={pending || !name.trim()}
            >
              {pending ? <Loader2 className="animate-spin" /> : <Save />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
